/**
 * useCart.js
 * Manages cart state: add, remove, update quantity, totals
 * Cart items come from either ProductLinkPricing (URL scrape) or ProductCatalog
 */

import { useState, useCallback } from "react";
import { calculatePrice } from "../utils/pricingEngine";

export function useCart() {
  const [cartItems, setCartItems] = useState([]);

  /**
   * Add a product to the cart or update qty if already exists
   * item shape: { id, name, brand, model, amazonPrice, priceExclGST, weight, imageUrl, source }
   */
  const addToCart = useCallback((product, quantity = 1, deliveryType = "normal") => {
    setCartItems((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.id === product.id
            ? { ...i, quantity, deliveryType }
            : i
        );
      }
      return [
        ...prev,
        {
          ...product,
          quantity,
          deliveryType,
          id: product.id || `${Date.now()}-${Math.random()}`,
        },
      ];
    });
  }, []);

  const removeFromCart = useCallback((id) => {
    setCartItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const updateQuantity = useCallback((id, quantity) => {
    if (quantity < 1) return;
    setCartItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, quantity } : i))
    );
  }, []);

  const updateDelivery = useCallback((id, deliveryType) => {
    setCartItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, deliveryType } : i))
    );
  }, []);

  const clearCart = useCallback(() => setCartItems([]), []);

  // ── Derived totals ────────────────────────────────────────────────────────
  // Uses new calculatePrice(basePriceExclGST, amazonPriceWithGST, weight, qty, deliveryType)
  // GST is NOT added here — it's noted as "applicable at billing" only
  const totals = cartItems.reduce(
    (acc, item) => {
      const basePriceExclGST   = item.priceExclGST ?? Math.round((item.amazonPrice ?? 0) / 1.18);
      const amazonPriceWithGST = item.amazonPrice ?? 0;

      const { finalPrice } = calculatePrice(
        basePriceExclGST,
        amazonPriceWithGST,
        item.weight,
        item.quantity,
        item.deliveryType
      );

      acc.subtotal  += finalPrice;
      acc.itemCount += item.quantity;
      return acc;
    },
    { subtotal: 0, itemCount: 0 }
  );

  return {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    updateDelivery,
    clearCart,
    subtotal:  totals.subtotal,
    itemCount: totals.itemCount,
  };
}